import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useClients, useTransportRefs } from '@/features/directories/api/useDirectory'
import { useQuotes } from '@/features/quotes/api/useQuote'
import type { CaseSummary, PaymentMethod, PaymentTermsKind } from '@/types/domain'

/* "New case" and "From quote".
 *
 * Creating a case WITHOUT a quote is a primary action (FR-70), not a fallback:
 * repeat business runs through it, and so do all ~241 migrated cases, which
 * arrive quote-less by definition. So this sheet stands on its own and does not
 * require a quote reference — the field is there for when one exists, and the
 * 1:1 link it creates is navigation and provenance (FR-19), not a prerequisite.
 *
 * Client and transport mode are DIRECTORY PICKS carrying ids, not typed text
 * (FR-39). That is the substitution the product exists to make — the source
 * workbook joined its sheets on a company name somebody retyped each time, and
 * the same client arrived as four spellings — and it is also what the server
 * accepts: POST /cases takes a `clientId` and a `transportModeId`, so a typed
 * name had nothing to send and the create could never leave the sheet.
 */

export interface CaseDraft {
  clientId: string
  origin: string
  destination: string
  transportModeId: string
  /* The quote's ID, not the number printed on it. Typed text was the same
     mistake the client field exists to undo: "ASA-Q-0042", "asa q 42" and a
     number off by a digit all read as a reference and none of them resolve to a
     record. A pick carries the id the 1:1 link (FR-19) is actually made of. */
  quoteId: string
  eta: string
  /* Whether the client's final packing list is in hand (FR-12). The sheet has
     no control for it and never sets it: this sheet OPENS cases, and a case
     opened by hand has no packing list yet, so false is the fact rather than a
     placeholder. It is on the draft all the same because the payload sends the
     whole record — an amend sheet built on this shape must prefill the case's
     own flag, and a field it has to fill is harder to miss than a field it
     would have had to remember to add. */
  finalPackingListReceived: boolean
  /* How this shipment gets paid for (FR-32). Three fields rather than one
     phrase, and both the kind and the method are REQUIRED: the server refuses a
     case without them, so a sheet that did not collect them could never create
     one — an unpaid shipment with no recorded terms is the argument this product
     exists to prevent.

     '' is the unchosen state and is deliberately representable. The field is
     mandatory and there is no default: an empty required select is honest,
     where a pre-picked kind would put dated expectations in front of a client on
     the strength of nobody having looked at the field. */
  paymentTermsKind: PaymentTermsKind | ''
  paymentMethod: PaymentMethod | ''
  /* Days, as TEXT while it is being typed — a half-typed "4" is not the number
     4. Meaningful only for `credit`, and cleared when the kind moves away from
     it, because the server refuses a day count on any other kind. */
  paymentTermsCreditDays: string
}

export function CaseFormSheet({
  open,
  onClose,
  record,
  /** Pre-fills the quote reference when the action was "From quote". */
  fromQuote,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: CaseSummary
  fromQuote?: boolean
  onSubmit: (values: CaseDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  // Searchable directory reads, so hundreds of entries stay usable. Each list is
  // the one this caller may see — the server decides that, not this sheet (AD-5).
  const { data: clients } = useClients()
  // `activeOnly`: a retired mode is not offered on a new case, though existing
  // cases keep the one they were opened with (FR-43).
  const { data: modes } = useTransportRefs('mode', true)
  /* Only fetched when this sheet is the "From quote" one — a plain create shows
     no quote field, and a list nobody can see is a request nobody asked for. */
  const { data: quotes } = useQuotes({ enabled: Boolean(open && fromQuote) })

  /* An EDIT seeds its two pickers by resolving the summary row's labels against
     the directories, so it cannot seed correctly until those have arrived. The
     reset key carries that readiness: the sheet seeds blank if it opens first
     and re-seeds once, with the real ids, when the lists land. Without it a
     sheet opened on a cold cache would quietly drop the client it was editing.
     A create has nothing to resolve and is unaffected either way. */
  const resolved = Boolean(clients && modes)
  const [draft, setDraft, touched, markTouched] = useFormDraft<CaseDraft>(
    open ? `${record?.id ?? 'new'}:${record && !resolved ? 'pending' : 'ready'}` : null,
    () =>
      record
        ? {
            // A summary row carries the client's NAME for display, not its id,
            // so an edit resolves the id back through the directory rather than
            // sending the label the row happened to render.
            clientId:
              clients?.find((client) => client.name === record.clientName)?.id ?? '',
            origin: record.origin,
            destination: record.destination,
            transportModeId: modes?.find((mode) => mode.code === record.mode)?.id ?? '',
            quoteId: '',
            eta: record.eta ?? '',
            /* Carried through untouched. The sheet shows no control for it, but
               the payload submits the whole record, so an amend that seeded
               false here would erase the flag conversion set the moment anybody
               corrected a typo in the destination. */
            finalPackingListReceived: record.finalPackingListReceived ?? false,
            paymentTermsKind: record.paymentTermsKind ?? '',
            paymentMethod: record.paymentMethod ?? '',
            paymentTermsCreditDays:
              record.paymentTermsCreditDays === undefined
                ? ''
                : String(record.paymentTermsCreditDays),
          }
        : emptyCase,
  )

  /* What may be converted: an ISSUED quote, which is the same set the server
     converts (quote.ReadForConversion). A draft was never offered, a declined
     one never will be, and a `converted` one already has its case — offering
     any of them invites a link the server would refuse (FR-19).

     `sent` rather than the `approved` this filter used to name: accepting an
     offer IS conversion since migration 0050, so there is no state between the
     two for a quote to wait in, and the acceptance is recorded by the act this
     sheet performs. Searched by number OR client, because the desk remembers
     whichever it last had in hand. */
  const quoteOptions = useMemo(
    () =>
      (quotes ?? [])
        .filter((quote) => quote.status === 'sent')
        .map((quote) => ({
          value: quote.id,
          label: quote.number,
          hint: quote.clientName,
        })),
    [quotes],
  )

  const clientError = touched && !draft.clientId ? t('common.form.required') : undefined
  const originError =
    touched && !draft.origin.trim() ? t('common.form.required') : undefined
  const destinationError =
    touched && !draft.destination.trim() ? t('common.form.required') : undefined
  // A quote reference is required only when this sheet was opened as
  // "From quote" — the whole point of FR-70 is that the plain path does not
  // need one.
  const quoteError =
    touched && fromQuote && !draft.quoteId ? t('common.form.required') : undefined
  const termsKindError =
    touched && !draft.paymentTermsKind ? t('common.form.required') : undefined
  const methodError =
    touched && !draft.paymentMethod ? t('common.form.required') : undefined
  /* Credit days are required exactly when the kind is `credit`, and refused on
     every other kind — the same rule in both directions the server enforces, so
     a case cannot arrive with a due date nobody agreed. */
  const creditDays = Number(draft.paymentTermsCreditDays)
  const creditDaysMissing =
    draft.paymentTermsKind === 'credit' &&
    (!draft.paymentTermsCreditDays.trim() ||
      !Number.isInteger(creditDays) ||
      creditDays < 1 ||
      creditDays > 365)
  const creditDaysError =
    touched && creditDaysMissing ? t('cases.form.creditDaysInvalid') : undefined
  const invalid =
    !draft.clientId ||
    !draft.origin.trim() ||
    !draft.destination.trim() ||
    !draft.paymentTermsKind ||
    !draft.paymentMethod ||
    creditDaysMissing ||
    Boolean(fromQuote && !draft.quoteId)

  const title = record
    ? t('cases.actions.editCase')
    : fromQuote
      ? t('cases.actions.fromQuote')
      : t('cases.actions.newCase')

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={title}
      submitLabel={record ? undefined : t('cases.actions.create')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {fromQuote ? (
        <Field
          label={t('cases.form.quoteNumber')}
          required
          error={quoteError}
          hint={t('cases.form.quoteHint')}
        >
          {(props) => (
            <Select
              value={draft.quoteId}
              onChange={(quoteId) => {
                const picked = quotes?.find((quote) => quote.id === quoteId)
                /* Picking the quote fills the case from it. A quote already
                   holds the client and, once route options exist, the lane —
                   retyping them here is how the two records drift apart. Only
                   EMPTY fields are filled, so a pick never overwrites something
                   already typed, and every one stays editable afterwards. */
                setDraft({
                  ...draft,
                  quoteId,
                  clientId: draft.clientId || (picked?.clientId ?? ''),
                  origin: draft.origin || (picked?.origin ?? ''),
                  destination: draft.destination || (picked?.destination ?? ''),
                  /* The payment terms are deliberately NOT carried across. A
                     quote's are a phrase somebody typed and a case's are one of
                     four named kinds, and there is no honest mapping between
                     them: guessing that "50/50" meant fifty_fifty would put two
                     dated expectations in front of a client on the strength of a
                     string match. The server's own conversion makes the same
                     refusal. */
                })
              }}
              options={quoteOptions}
              placeholder={t('common.form.selectPlaceholder')}
              searchable
              {...props}
            />
          )}
        </Field>
      ) : null}

      <Field label={t('cases.columns.client')} required error={clientError}>
        {(props) => (
          <Select
            value={draft.clientId}
            onChange={(clientId) => setDraft({ ...draft, clientId })}
            options={(clients ?? []).map((client) => ({
              value: client.id,
              label: client.name,
              hint: client.code,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        {/* Countries, the same ISO 3166-1 pickers the quote's route options use
            — a case opened from a quote inherits that quote's endpoints, so the
            two surfaces must speak in the same units or the carry across would
            put a code in a field that expects a port and back again.

            What it costs, and it is the same cost RouteOptionsCard records: a
            case opened through this sheet names a COUNTRY where the ~241
            migrated cases name a city. Both render — every reading surface goes
            through `placeName`, which passes an unrecognised value through as
            written — and neither is rewritten. */}
        <Field
          label={t('quotes.form.origin')}
          required
          error={originError}
          className="flex-1"
        >
          {(props) => (
            <CountryDropdown
              value={draft.origin}
              onChange={(origin) => setDraft({ ...draft, origin })}
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.form.destination')}
          required
          error={destinationError}
          className="flex-1"
        >
          {(props) => (
            <CountryDropdown
              value={draft.destination}
              onChange={(destination) => setDraft({ ...draft, destination })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Not required: the server takes a case with no mode set yet, and the
          desk often opens one before the routing is decided. */}
      <Field label={t('quotes.form.mode')}>
        {(props) => (
          <Select
            value={draft.transportModeId}
            onChange={(transportModeId) => setDraft({ ...draft, transportModeId })}
            options={(modes ?? []).map((mode) => ({
              value: mode.id,
              // The directory's own label, not a translated code: transport refs
              // are maintained by the desk (FR-43), so entries exist that no
              // catalogue key covers.
              label: mode.label,
              hint: mode.code,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      {/* ETA only, and no ETD beside it.
          A case header carries the day it opened and the day the cargo is
          expected; the DEPARTURE belongs to a leg (FR-24), and there is no
          header field to put one in. An ETD picker here collected a date the
          save then dropped — a field that appears to record something and does
          not is worse than an absent one. It returns with the legs. */}
      <Field label={t('cases.columns.eta')}>
        {(props) => (
          <DatePicker
            value={draft.eta}
            onChange={(eta) => setDraft({ ...draft, eta })}
            {...props}
          />
        )}
      </Field>

      {/* Required, unlike the same field on a quote: a quote is an offer that
          may still be negotiated, but a case is the shipment being run, and
          FR-32 makes the terms it runs under a matter of record.

          A pick from four rather than free text, because the terms have to
          become DATED EXPECTATIONS the platform can chase (FR-33) and no parse
          of a phrase somebody typed yields two dated amounts you would put in
          front of a client. */}
      <Field
        label={t('cases.form.paymentTermsKind')}
        required
        error={termsKindError}
      >
        {(props) => (
          <Select
            value={draft.paymentTermsKind}
            onChange={(kind) =>
              setDraft({
                ...draft,
                paymentTermsKind: kind as PaymentTermsKind,
                /* Cleared when the kind moves off `credit`: the server refuses
                   a day count on any other kind, and a number left behind here
                   would be read later as a due date. */
                paymentTermsCreditDays:
                  kind === 'credit' ? draft.paymentTermsCreditDays : '',
              })
            }
            options={termsKinds.map((kind) => ({
              value: kind,
              label: t(`cases.terms.${kind}`),
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      {/* Only for `credit`, where the days ARE the agreement — "net 30" and
          "net 45" are the same arrangement at two numbers. A numeric keyboard
          on the phone (UX-DR-27): this field takes digits and nothing else. */}
      {draft.paymentTermsKind === 'credit' ? (
        <Field
          label={t('cases.form.creditDays')}
          required
          error={creditDaysError}
          hint={t('cases.form.creditDaysHint')}
        >
          {(props) => (
            <Input
              value={draft.paymentTermsCreditDays}
              inputMode="numeric"
              onChange={(e) =>
                setDraft({ ...draft, paymentTermsCreditDays: e.target.value })
              }
              {...props}
            />
          )}
        </Field>
      ) : null}

      {/* How the money actually arrives. Operational rather than accounting:
          cash is collected by a person and a transfer is chased through a bank,
          which is different people and different follow-up. */}
      <Field label={t('cases.form.paymentMethod')} required error={methodError}>
        {(props) => (
          <Select
            value={draft.paymentMethod}
            onChange={(method) =>
              setDraft({ ...draft, paymentMethod: method as PaymentMethod })
            }
            options={paymentMethods.map((method) => ({
              value: method,
              label: t(`cases.method.${method}`),
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('cases.form.nextStep')}
      </p>
    </FormSheet>
  )
}

/* The four kinds and the two methods, in the order the desk reads them. Listed
   here rather than derived from the catalogue so that a kind added to one
   without the other fails the compile rather than rendering a blank option. */
const termsKinds: PaymentTermsKind[] = [
  'fifty_fifty',
  'full_on_delivery',
  'credit',
  'other',
]
const paymentMethods: PaymentMethod[] = ['bank_transfer', 'cash']

const emptyCase: CaseDraft = {
  clientId: '',
  origin: '',
  destination: '',
  transportModeId: '',
  quoteId: '',
  eta: '',
  finalPackingListReceived: false,
  /* No default kind and no default method. The field is mandatory and an empty
     required select is honest — a default would be terms nobody chose. */
  paymentTermsKind: '',
  paymentMethod: '',
  paymentTermsCreditDays: '',
}
